import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useSpring,
  useTransform,
} from "framer-motion";
import {
  X,
  User,
  Crown,
  Building2,
  Sparkles,
  Clock,
  Zap,
  Globe,
  FileText,
  Mail,
  Star,
  Coins,
  Users,
  Workflow,
  ShieldCheck,
  ArrowRight,
} from "lucide-react";
import API from "../api/api";
import { setCredits } from "../store/userSlice";
import { useLanguage, getTranslation } from "../utils/language";
import "../style/subscription.css";
import { GRGPT_GOLD_LOGO } from "../assets/logoBase64";

// Stacked Coins Icon matching the reference design exactly
function CoinStackIcon({ size = 34, color = "#B88A45", className = "" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      {/* Coin 1 (top) */}
      <ellipse cx="12" cy="5.8" rx="8" ry="2.6" fill={color} />

      {/* Coin 2 (middle) */}
      <path
        d="M4 8.6v3.2c0 1.44 3.58 2.6 8 2.6s8-1.16 8-2.6V8.6c-1.6 1.1-4.5 1.7-8 1.7s-6.4-.6-8-1.7z"
        fill={color}
      />

      {/* Coin 3 (bottom) */}
      <path
        d="M4 14.6v3.2c0 1.44 3.58 2.6 8 2.6s8-1.16 8-2.6v-3.2c-1.6 1.1-4.5 1.7-8 1.7s-6.4-.6-8-1.7z"
        fill={color}
      />
    </svg>
  );
}

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.05,
    },
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.15 },
  },
};

const cardVariants = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 120,
      damping: 18,
      mass: 0.8,
    },
  },
};

const TERMS_DATA = {
  "plan-basic": {
    title: "Basic Plan — Terms & Conditions",
    sections: [
      {
        heading: "Credit Usage",
        items: [
          "Includes 10 credits per day.",
          "Daily credits reset every day according to the platform's defined reset time.",
          "Unused daily credits do not carry forward to the next day.",
        ],
      },
      {
        heading: "Plan Access",
        items: [
          "Basic access includes only the features available under the Basic plan.",
          "Usage is subject to GR-GPT's fair-use and acceptable-use policies.",
          "GR-GPT may impose reasonable usage limits to prevent abuse or automated misuse.",
        ],
      },
      {
        heading: "General",
        items: [
          "No payment or credit card is required for the Basic plan.",
          "GR-GPT may modify Basic plan features or credit limits with reasonable notice.",
          "Credits have no cash value and cannot be transferred, sold, or redeemed for cash.",
        ],
      },
    ],
  },
  "plan-plus": {
    title: "Plus Plan — Terms & Conditions",
    sections: [
      {
        heading: "Credit Usage & Pricing",
        items: [
          "Includes 5,000 credits per month.",
          "Credits are allocated according to the monthly billing cycle.",
          "Unused credits do not roll over to the next billing cycle unless explicitly stated by GR-GPT.",
          "The plan costs ₹499 per month, inclusive of applicable GST.",
          "The standard price is ₹600, with the current discounted price applicable while the offer is active.",
        ],
      },
      {
        heading: "Renewal & Cancellation",
        items: [
          "Subscription renews automatically each month unless cancelled before the next billing date.",
          "Cancellation stops the next renewal; access generally continues until the end of the current paid billing period.",
          "Refunds, if applicable, are subject to GR-GPT's refund policy.",
        ],
      },
      {
        heading: "General",
        items: [
          "Plus features and credit limits are subject to fair-use and acceptable-use policies.",
          "Credits cannot be transferred, sold, exchanged, or redeemed for cash.",
          "GR-GPT may change pricing, features, or credit limits with reasonable notice.",
        ],
      },
    ],
  },
  "plan-pro": {
    title: "Pro Plan — Terms & Conditions",
    sections: [
      {
        heading: "Credit Usage & Pricing",
        items: [
          "Includes 60,000 credits per year.",
          "Credits are allocated for the annual subscription period.",
          "Unused credits do not roll over beyond the annual subscription period unless explicitly stated by GR-GPT.",
          "The plan costs ₹4,999 per year, inclusive of applicable GST.",
          "The standard price is ₹7,200, with the current discounted price applicable while the offer is active.",
        ],
      },
      {
        heading: "Renewal & Cancellation",
        items: [
          "Subscription renews automatically annually unless cancelled before the next renewal date.",
          "Cancellation prevents the next annual renewal; access generally continues until the end of the current paid period.",
          "Refunds, if applicable, are subject to GR-GPT's refund policy.",
        ],
      },
      {
        heading: "General",
        items: [
          "Pro features and credit usage remain subject to fair-use and acceptable-use policies.",
          "Credits cannot be transferred, sold, exchanged, or redeemed for cash.",
          "Advanced features, integrations, and priority support may be subject to availability and technical limitations.",
          "GR-GPT may change pricing, features, or credit limits with reasonable notice.",
        ],
      },
    ],
  },
};

export default function Subscription() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.user);
  const [language] = useLanguage();
  const [apiPlans, setApiPlans] = useState([]);
  const [activePlanName, setActivePlanName] = useState("Basic");
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [credits, setCreditsState] = useState(0);
  const [loading, setLoading] = useState(true);
  const [buyingId, setBuyingId] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");
  const [termsPlanId, setTermsPlanId] = useState(null);

  // Lock scroll and support Escape key when Terms modal is open
  useEffect(() => {
    if (!termsPlanId) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setTermsPlanId(null);
      }
    };

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [termsPlanId]);

  const gridRef = useRef(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  // 60fps Spring Interpolation for subtle Mouse Parallax
  const smoothX = useSpring(mouseX, { stiffness: 120, damping: 20 });
  const smoothY = useSpring(mouseY, { stiffness: 120, damping: 20 });

  const cardX0 = useTransform(smoothX, [-1, 1], [-2.5, 2.5]);
  const cardY0 = useTransform(smoothY, [-1, 1], [-2.5, 2.5]);
  const cardX1 = useTransform(smoothX, [-1, 1], [-4, 4]);
  const cardY1 = useTransform(smoothY, [-1, 1], [-4, 4]);
  const cardX2 = useTransform(smoothX, [-1, 1], [-2.8, 2.8]);
  const cardY2 = useTransform(smoothY, [-1, 1], [-2.8, 2.8]);

  const cardTransforms = [
    { x: cardX0, y: cardY0 },
    { x: cardX1, y: cardY1 },
    { x: cardX2, y: cardY2 },
  ];

  const handleMouseMove = (e) => {
    if (!gridRef.current) return;
    const rect = gridRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const dx = (e.clientX - centerX) / (rect.width / 2);
    const dy = (e.clientY - centerY) / (rect.height / 2);
    mouseX.set(Math.max(-1, Math.min(1, dx)));
    mouseY.set(Math.max(-1, Math.min(1, dy)));
  };

  const handleMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
  };

  useEffect(() => {
    loadPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPlans = async () => {
    try {
      const planRes = await API.get("/subscription/plans");
      const plansData = planRes.data?.plans || planRes.data || [];
      setApiPlans(plansData);

      if (user) {
        const creditRes = await API.get("/user/credits");
        const newCredits = creditRes.data?.credits || 0;

        setCreditsState(newCredits);
        dispatch(setCredits(newCredits));

        // Fetch user active subscription
        try {
          const subRes = await API.get("/subscription/my-subscription");
          if (subRes.data?.planName) {
            setActivePlanName(subRes.data.planName);
          }
          if (subRes.data?.hasActiveSubscription !== undefined) {
            setHasActiveSubscription(Boolean(subRes.data.hasActiveSubscription));
          }
        } catch (e) {
          console.error("Failed to fetch active subscription:", e);
        }
      }
    } catch (err) {
      console.error("Subscription load error", err);
    } finally {
      setLoading(false);
    }
  };

  const buyPlan = async (planToBuy) => {
    if (!user) {
      alert("Please log in to upgrade your subscription.");
      navigate("/login");
      return;
    }

    if (planToBuy.priceNum === 0 || planToBuy.name.toLowerCase() === "basic") {
      navigate("/");
      return;
    }

    const matchedApiPlan = apiPlans.find(
      (p) =>
        p._id === planToBuy._id ||
        p.name?.toLowerCase() === planToBuy.name?.toLowerCase() ||
        (planToBuy.name === "Plus" && (p.name?.toLowerCase() === "professional" || p.name?.toLowerCase() === "plus")) ||
        (planToBuy.name === "Pro" && (p.name?.toLowerCase() === "enterprise" || p.name?.toLowerCase() === "pro"))
    );

    const targetPlanId = matchedApiPlan ? matchedApiPlan._id : (planToBuy._id || planToBuy.id);
    const planIdentifier = targetPlanId || planToBuy.id || planToBuy.name;

    try {
      setBuyingId(planIdentifier);
      setSuccessMsg("🔄 Connecting to PhonePe payment gateway...");

      const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      const isAndroid = /Android/i.test(navigator.userAgent);
      const deviceContext = isIOS
        ? { deviceOS: "IOS" }
        : isAndroid
          ? { deviceOS: "ANDROID" }
          : null;

      const res = await API.post("/payment/phonepe/initiate", {
        planId: targetPlanId,
        planName: planToBuy.name,
        deviceContext,
      });

      if (res.data?.success && res.data?.redirectUrl) {
        setSuccessMsg("✅ Redirecting to PhonePe secure checkout...");
        window.location.href = res.data.redirectUrl;
      } else {
        setSuccessMsg("❌ " + (res.data?.message || "Could not initiate PhonePe payment"));
        setTimeout(() => setSuccessMsg(""), 5000);
      }
    } catch (err) {
      console.error("Payment initiation error:", err);
      let errMsg = "❌ Payment initiation failed. Please try again.";
      if (err.response?.status === 401) {
        errMsg = "❌ You must be logged in to purchase a plan. Please log in first.";
        navigate("/login");
      } else if (err.response?.status === 404) {
        errMsg = "❌ Payment endpoint not found (404). Please restart the backend server.";
      } else if (err.response?.data?.message) {
        errMsg = "❌ " + err.response.data.message;
      }
      setSuccessMsg(errMsg);
      alert(errMsg);
      setTimeout(() => setSuccessMsg(""), 6000);
    } finally {
      setBuyingId(null);
    }
  };

  // 3-Card Pricing Specifications from user prompt and reference design
  const plans = [
    {
      id: "plan-basic",
      name: "Basic",
      badge: "Get Started",
      HeaderIcon: User,
      description: "Perfect for casual users and quick research.",
      price: "FREE",
      priceNum: 0,
      priceOld: null,
      pricePeriod: null,
      creditNum: "10",
      creditPeriod: "Credits per day",
      features: [
        { icon: Sparkles, text: "Access to core GR-GPT features" },
        { icon: Clock, text: "10 credits every day" },
        { icon: Zap, text: "Standard response speed" },
        { icon: Globe, text: "Web access for real-time insights" },
        { icon: FileText, text: "Basic chat history" },
        { icon: Mail, text: "Email support" },
      ],
      ctaText: "Get Started",
      supportingText: "No credit card required",
      isPopular: false,
    },
    {
      id: "plan-plus",
      name: "Plus",
      badge: "Best Value",
      popularBadge: "★ Most Popular",
      HeaderIcon: Crown,
      description: "For power users who need more credits and advanced features.",
      price: "₹499",
      priceNum: 499,
      priceOld: "₹600",
      pricePeriod: "per month (inclusive of GST)",
      creditNum: "5,000",
      creditPeriod: "Credits per month",
      features: [
        { icon: Star, text: "Access to all core features" },
        { icon: Coins, text: "5,000 credits every month" },
        { icon: Zap, text: "Faster response speed" },
        { icon: Clock, text: "Extended chat history" },
        { icon: FileText, text: "Export chats & citations" },
        { icon: Mail, text: "Priority email support" },
      ],
      ctaText: "Upgrade to Plus",
      supportingText: "Billed monthly • Cancel anytime",
      isPopular: true,
    },
    {
      id: "plan-pro",
      name: "Pro",
      badge: "For Professionals",
      HeaderIcon: Building2,
      description: "For professionals and teams who need maximum usage.",
      price: "₹4,999",
      priceNum: 4999,
      priceOld: "₹7,200",
      pricePeriod: "per year (inclusive of GST)",
      creditNum: "60,000",
      creditPeriod: "Credits per year",
      features: [
        { icon: Crown, text: "Everything in Plus" },
        { icon: Coins, text: "60,000 credits every year" },
        { icon: Zap, text: "Fastest response speed" },
        { icon: Users, text: "Priority support" },
        { icon: Workflow, text: "Advanced tools & integrations" },
        { icon: FileText, text: "Full chat history with export" },
        { icon: ShieldCheck, text: "Dedicated account manager" },
      ],
      ctaText: "Upgrade to Pro",
      supportingText: "Billed yearly • Cancel anytime",
      isPopular: false,
    },
  ];

  const getActivePlanId = () => {
    // If the user is logged in and has an active paid subscription, resolve Plus or Pro
    if (user && hasActiveSubscription) {
      const norm = (activePlanName || "").trim().toLowerCase();

      // 1. Pro / Enterprise Tier (Matches "enterprise", "pro", "department pro", but NOT "professional")
      if (
        norm.includes("enterprise") ||
        norm === "pro" ||
        norm.startsWith("pro ") ||
        norm.endsWith(" pro") ||
        /\bpro\b/.test(norm)
      ) {
        return "plan-pro";
      }

      // 2. Plus / Professional Tier (Matches "professional", "plus", "govtech team")
      if (
        norm.includes("professional") ||
        norm.includes("plus") ||
        norm.includes("govtech")
      ) {
        return "plan-plus";
      }
    }

    // Initially for all users, Basic is their current plan
    return "plan-basic";
  };

  if (loading) {
    return (
      <div className="sub-page-container" style={{ justifyContent: "center" }}>
        Loading plans...
      </div>
    );
  }

  return (
    <div className="sub-page-container">
      {/* Top Close Button */}
      <motion.button
        type="button"
        onClick={() => navigate("/")}
        className="sub-close-btn"
        title="Back to Chat"
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
      >
        <X size={18} />
      </motion.button>

      {/* Header Section (Preserved) */}
      <motion.div
        className="sub-header"
        initial={{ opacity: 0, y: -20, filter: "blur(6px)" }}
        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
        transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
      >
        {/* GR Logo Mark Badge */}
        <div className="sub-logo-badge">
          <img
            src={GRGPT_GOLD_LOGO}
            alt="GRGPT Logo"
            className="sub-logo-img"
          />
        </div>

        <h1 className="sub-title">
          {getTranslation(language, "subscription", "title") || "Subscription Plans"}
        </h1>

        <p className="sub-subtitle">
          {getTranslation(language, "subscription", "subtitle") ||
            "Choose the right plan for your government document research needs."}
        </p>

        {/* User Credits Balance */}
        {user && (
          <div className="sub-credits-badge">
            <span>Current Balance:</span>
            <span className="sub-credits-amount">{credits} credits</span>
          </div>
        )}

        {/* Status Message */}
        {successMsg && <div className="sub-status-msg">{successMsg}</div>}
      </motion.div>

      {/* Cards Grid with Framer Motion Stagger & Subtle Parallax */}
      <AnimatePresence mode="wait">
        <motion.div
          key="personal-plans"
          ref={gridRef}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          className="sub-plans-grid"
          variants={containerVariants}
          initial="hidden"
          animate="show"
          exit="exit"
        >
          {plans.map((plan, index) => {
            const cardTransform = cardTransforms[index] || cardTransforms[0];
            const activePlanId = getActivePlanId();
            const isCurrent = activePlanId === plan.id;
            const HeaderIcon = plan.HeaderIcon;
            const isBuyingThis = buyingId && (buyingId === plan.id || buyingId === plan.name);

            return (
              <motion.div
                key={plan.id}
                custom={index}
                variants={cardVariants}
                style={{ x: cardTransform.x, y: cardTransform.y }}
                whileHover={{
                  y: plan.isPopular ? -6 : -4,
                  transition: { duration: 0.22, ease: "easeOut" },
                }}
                className={`sub-pricing-card ${plan.isPopular ? "sub-card--popular" : ""}`}
              >
                {/* Popular Badge above Plus Card */}
                {plan.isPopular && (
                  <div className="sub-popular-badge">
                    <Star size={13} fill="#FFFFFF" color="#FFFFFF" />
                    <span>Most Popular</span>
                  </div>
                )}

                {/* Card Content */}
                <div className="sub-card-content">
                  {/* Header Row: Icon and Pill Badge */}
                  <div className="sub-card-top-row">
                    <div className="sub-card-icon-wrap">
                      <HeaderIcon size={22} strokeWidth={1.8} />
                    </div>
                    <span className="sub-card-pill-badge">{plan.badge}</span>
                  </div>

                  {/* Plan Name & Description */}
                  <h2 className="sub-card-name">{plan.name}</h2>
                  <p className="sub-card-description">{plan.description}</p>

                  {/* Pricing Display */}
                  <div className="sub-card-price-section">
                    <div className="sub-card-price-main">
                      <span className="sub-card-price-val">{plan.price}</span>
                      {plan.priceOld && (
                        <span className="sub-card-price-old">{plan.priceOld}</span>
                      )}
                    </div>
                    {plan.pricePeriod ? (
                      <span className="sub-card-price-period">{plan.pricePeriod}</span>
                    ) : (
                      <span className="sub-card-price-period sub-card-price-period--free">
                        Free forever (inclusive of GST)
                      </span>
                    )}
                  </div>

                  {/* Highlighted Credit Allowance Box */}
                  <div className="sub-credit-highlight-box">
                    <div className="sub-credit-info">
                      <div className="sub-credit-num">{plan.creditNum}</div>
                      <div className="sub-credit-label">{plan.creditPeriod}</div>
                    </div>
                    <CoinStackIcon size={34} color="#B88A45" className="sub-credit-coins" />
                  </div>

                  {/* Feature List */}
                  <ul className="sub-feature-list">
                    {plan.features.map((feat, idx) => {
                      const FeatIcon = feat.icon;
                      return (
                        <li key={idx} className="sub-feature-item">
                          <FeatIcon size={18} className="sub-feature-icon" strokeWidth={1.9} />
                          <span>{feat.text}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                {/* Card Bottom CTA & Supporting Text */}
                <div className="sub-card-bottom">
                  {isCurrent ? (
                    <button type="button" disabled className="sub-card-cta sub-card-cta--current">
                      Your current plan
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => buyPlan(plan)}
                      disabled={!!buyingId}
                      className="sub-card-cta"
                    >
                      <span>
                        {isBuyingThis ? "Connecting to PhonePe..." : plan.ctaText}
                      </span>
                      <ArrowRight size={17} strokeWidth={2.2} className="sub-cta-arrow" />
                    </button>
                  )}
                  <p className="sub-card-support-text">{plan.supportingText}</p>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setTermsPlanId(plan.id);
                    }}
                    className="sub-card-terms-link"
                  >
                    Terms & Conditions
                  </button>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </AnimatePresence>

      {/* Reusable Terms & Conditions Modal */}
      <AnimatePresence>
        {termsPlanId && TERMS_DATA[termsPlanId] && (
          <motion.div
            className="sub-terms-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setTermsPlanId(null)}
          >
            <motion.div
              className="sub-terms-modal"
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="sub-terms-header">
                <h3 className="sub-terms-title">{TERMS_DATA[termsPlanId].title}</h3>
                <button
                  type="button"
                  className="sub-terms-close-btn"
                  onClick={() => setTermsPlanId(null)}
                  aria-label="Close Terms"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Body */}
              <div className="sub-terms-body">
                {TERMS_DATA[termsPlanId].sections.map((sec, sIdx) => (
                  <div key={sIdx} className="sub-terms-section">
                    <h4 className="sub-terms-heading">{sec.heading}</h4>
                    <ul className="sub-terms-list">
                      {sec.items.map((item, iIdx) => (
                        <li key={iIdx} className="sub-terms-item">
                          <span className="sub-terms-bullet">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="sub-terms-footer">
                <button
                  type="button"
                  className="sub-terms-action-btn"
                  onClick={() => setTermsPlanId(null)}
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}